import { Routes, Route, useLocation } from "react-router-dom";
import "./App.css";
import "./Buttons.css";
import Auth from "./routes/Auth";
import React, { useState, useEffect } from "react";
import Internet from "./assets/gif/internet-down.gif";
import Server from "./assets/gif/server-down.gif";
import "bootstrap/dist/css/bootstrap.min.css";

import PageNotFound from "./pages/PageNotFound";
import { routingData } from "./routes/routing";
import RedirectRouter from "./routes/RedirectRouter";

import Fireworks from "./Fire";
import BDAIChat from "./components/BDAIChat"; // ✅ BD AI assistant
import { BASE_URL } from "./config/axios"; // named export (NOT "import BASE_URL from")

// =====================================================================
// BD AI ASSISTANT SETTINGS
// =====================================================================

// Same Django server as the rest of the app (from src/config/axios.js).
const BDAI_API_URL = BASE_URL;
console.log("BD AI server ->", BDAI_API_URL);

// Pages where the floating assistant should NOT appear (login, signup, ...).
// Remove "/" if your dashboard lives at "/" after login.
const BDAI_HIDDEN_PATHS = ["/", "/login", "/signup", "/forgot-password", "/reset-password"];

// Your login stores the token in sessionStorage "access_token" (same as AUTH in config/axios.js).
// emp code / name: the first key that has a value wins - put your real key name first if different.
const TOKEN_KEYS = ["access_token"];
const USER_KEYS = ["user", "userData", "user_details", "userInfo"];
const EMP_CODE_KEYS = ["emp_code", "empCode", "employee_code"];
const NAME_KEYS = ["name", "username", "user_name", "emp_name"];
// Role is stored in sessionStorage under "role". Only these roles see the BD AI chat.
const ROLE_KEYS = ["role"];
const BDAI_ALLOWED_ROLES = ["CertificationAccess"];

const getBDAIUser = () => {
  let user = {};
  try {
    user = JSON.parse(readStorage(USER_KEYS) || "{}") || {};
  } catch (e) {
    user = {};
  }
  const fullName = user.name || user.full_name || user.first_name || readStorage(NAME_KEYS) || "";
  return {
    token: readStorage(TOKEN_KEYS),
    role: String(readStorage(ROLE_KEYS) || "").replace(/^"|"$/g, "").trim(),
    empCode: user.emp_code || user.empCode || readStorage(EMP_CODE_KEYS) || "",
    firstName: String(fullName).trim().split(" ")[0] || "",
  };
};

// checks sessionStorage first (where your token lives), then localStorage
const readStorage = (keys) => {
  for (const store of [sessionStorage, localStorage]) {
    try {
      for (const k of keys) {
        const v = store.getItem(k);
        if (v && v !== "null" && v !== "undefined") return v;
      }
    } catch (e) {
      /* storage blocked */
    }
  }
  return null;
};



// Defined outside the component so it never changes between renders.
// It reads the token on every request (unlike AUTH in config/axios.js, which is read once at
// page load), so a token set after login or refreshed later is picked up automatically.
const getBDAIAuthHeaders = () => {
  const { token } = getBDAIUser();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// =====================================================================

function App() {
  const location = useLocation(); // re-render on route change -> assistant hides on login, shows after
  const bdaiUser = getBDAIUser();
const showBDAI =
  !!bdaiUser.token &&
  BDAI_ALLOWED_ROLES.includes(bdaiUser.role) &&
  !BDAI_HIDDEN_PATHS.includes(location.pathname.replace(/\/+$/, "") || "/");
  const recursiveRouter = (props) => {
    const { nestedRoutes, routes, isProtected, role } = props;
    const authenticatedRouted = () => (
      <>
        {routes &&
          routes.map((e, index) => (
            <Route key={index} path={e.path} element={e.component} />
          ))}
        {nestedRoutes &&
          nestedRoutes.map((e, index) => (
            <Route path={e.path} element={e.component} key={index}>
              {recursiveRouter(e)}
            </Route>
          ))}
      </>
    );

    return isProtected ? (
      <Route element={<Auth allowedRoles={role} />}>{authenticatedRouted()}</Route>
    ) : role === "" ? (
      <Route element={<RedirectRouter />}>{authenticatedRouted()}</Route>
    ) : (
      <>{authenticatedRouted()}</>
    );
  };

  // Session and visibility logic...
  useEffect(() => {
    const sessionStartTime = localStorage.getItem("session_start_time");
    if (!sessionStartTime) localStorage.setItem("session_start_time", Date.now());

    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        localStorage.removeItem("session_token");
        localStorage.removeItem("session_start_time");
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, []);

  useEffect(() => {
    const checkSessionDuration = () => {
      const sessionStartTime = localStorage.getItem("session_start_time");
      if (sessionStartTime) {
        const elapsedTime = Date.now() - parseInt(sessionStartTime, 10);
        if (elapsedTime >= 10800000) {
          localStorage.removeItem("session_token");
          localStorage.removeItem("session_start_time");
          window.location.reload();
        }
      }
    };
    const interval = setInterval(checkSessionDuration, 60000);
    return () => clearInterval(interval);
  }, []);

  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const serverOnline = true;

  useEffect(() => {
    const handleOnlineStatus = () => setIsOnline(navigator.onLine);
    window.addEventListener("online", handleOnlineStatus);
    window.addEventListener("offline", handleOnlineStatus);
    return () => {
      window.removeEventListener("online", handleOnlineStatus);
      window.removeEventListener("offline", handleOnlineStatus);
    };
  }, []);

  return (
    <>
      {/* ✅ Fireworks overlay */}
      {/* <Fireworks /> */}

      {isOnline ? (
        serverOnline ? (
          <>
            <Routes>
              {routingData.map((e, index) => (
                <React.Fragment key={index.toString()}>{recursiveRouter(e)}</React.Fragment>
              ))}
              <Route path="*" element={<PageNotFound />} />
            </Routes>

            {/* ✅ BD AI assistant - floats bottom-right on every page after login */}
            <BDAIChat
              apiBaseUrl={BDAI_API_URL}
              getAuthHeaders={getBDAIAuthHeaders}
              empCode={bdaiUser.empCode}
              userName={bdaiUser.firstName}
              hidden={!showBDAI}
            />
          </>
        ) : (
          <center>
            <br /><br /><br /><br /><br /><br /><br /><br />
            <p>
              You Are Online but, the Server is Offline. <br />
              <b>Please be Patient. We are working hard to fix it.</b> <br /><br />
              <img
                style={{
                  borderRadius: "20px",
                  boxShadow: "0px 1px 38px 0px rgba(0, 0, 0, 0.10)",
                }}
                src={Server}
                alt=""
              />
            </p>
          </center>
        )
      ) : (
        <center>
          <br /><br /><br /><br /><br /><br /><br /><br />
          <p>
            You are offline. Because Internet connection Is Not Working. <br />
            <b>Our Software is working Fine :)</b> <br /><br />
            <img
              style={{
                borderRadius: "20px",
                boxShadow: "0px 1px 38px 0px rgba(0, 0, 0, 0.10)",
              }}
              src={Internet}
              alt=""
            />
          </p>
        </center>
      )}
    </>
  );
}

export default App;